/**
 * Runtime-choice shell. GameView mounts it only for the chooser and keys it by
 * choiceId; TargetPicker owns the local generic selection state. Opaque tokens
 * are passed back to the action adapter and never rendered here.
 */
import { useId, useRef, type ReactElement } from 'react';
import type { SerializedChoice } from '../../server/protocol.js';
import { Modal } from './kit/index.js';
import { choiceKindLabel } from './locale.js';
import { TargetPicker } from './TargetPicker.js';

export interface ChoicePromptProps {
  readonly choice: SerializedChoice;
  readonly onConfirm: (choiceTokens: readonly string[]) => void;
}

export function ChoicePrompt({ choice, onConfirm }: ChoicePromptProps): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement | null>(null);
  const prompt = choice.prompt?.trim();
  const kindLabel = choice.kind === undefined ? undefined : choiceKindLabel(choice.kind);
  const title = prompt !== undefined && prompt.length > 0
    ? prompt
    : kindLabel === undefined
      ? 'Сделайте выбор'
      : `Выбор: ${kindLabel}`;
  const description = prompt !== undefined && prompt.length > 0 && kindLabel !== undefined
    ? `Тип выбора: ${kindLabel}`
    : undefined;

  return (
    <Modal
      layer="choice"
      overlayClassName="cards-choice-overlay"
      overlayTestId="choice-prompt"
      labelledBy={titleId}
      describedBy={descriptionId}
      externalDialogRef={dialogRef}
      closeOnBackdrop={false}
      closeOnEscape={false}
      lockBodyScroll={false}
    >
      <TargetPicker
        candidates={choice.candidates}
        cardinality={choice.cardinality}
        ordered={choice.ordered}
        title={title}
        {...(description === undefined ? {} : { description })}
        onConfirm={onConfirm}
        dialogTitleId={titleId}
        dialogDescriptionId={descriptionId}
        modalDialogRef={dialogRef}
      />
    </Modal>
  );
}
